
import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Briefcase, GraduationCap, MessageSquare, Calendar, Building, School } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Define types for Alumni data
interface AlumniProfileData {
  id: string;
  title?: string;
  company?: string;
  school?: string;
  years_experience?: number;
  expertise?: string;
  degree?: string;
  field_of_study?: string;
  graduation_year?: number;
  profiles?: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    bio?: string;
  };
  availability_status?: string;
}

interface ServiceData {
  id: string;
  title: string;
  description?: string;
  price: number;
  duration?: number;
}

interface ReviewData {
  id: string;
  rating: number;
  comment?: string;
  created_at: string;
  user_id: string;
  profiles: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

const AlumniProfile = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // For booking and messaging
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  
  // Try to use state passed from card if available
  const alumniFromState = location.state?.alumniData;

  // Fetch alumni data if we don't have it from state or need fresh data
  const fetchAlumniProfile = async () => {
    console.log("Fetching alumni profile for ID:", id);

    // Fetch the alumni profile
    const { data: profile, error: profileError } = await supabase
      .from("alumni_profiles")
      .select(`
        *,
        profiles:id(first_name, last_name, avatar_url, bio)
      `)
      .eq("id", id)
      .single();

    if (profileError) {
      console.error("Error fetching alumni profile:", profileError);
      throw new Error("Failed to fetch alumni profile");
    }

    // Fetch alumni services
    const { data: services, error: servicesError } = await supabase
      .from("alumni_services")
      .select("*")
      .eq("alumni_id", id);

    if (servicesError) {
      console.error("Error fetching services:", servicesError);
      throw new Error("Failed to fetch services");
    }

    // Fetch alumni reviews
    const { data: reviews, error: reviewsError } = await supabase
      .from("reviews")
      .select(`
        *,
        profiles:user_id(first_name, last_name, avatar_url)
      `)
      .eq("alumni_id", id);

    if (reviewsError) {
      console.error("Error fetching reviews:", reviewsError);
      throw new Error("Failed to fetch reviews");
    }

    console.log("Retrieved profile data:", { profile, services, reviews });
    return { profile, services, reviews };
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["alumniProfile", id],
    queryFn: fetchAlumniProfile,
    enabled: !!id && !alumniFromState, // Only fetch if we don't have data from state
  });

  const handleBookingRequest = async () => {
    try {
      // Check if user is logged in
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to book a session",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }

      const userId = session.session.user.id;
      const serviceData = data?.services?.find(service => service.id === selectedService);
      
      if (!serviceData) {
        toast({
          title: "Service not found",
          description: "The selected service could not be found. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Create booking record
      const { data: bookingData, error: bookingError } = await supabase
        .from("bookings")
        .insert({
          user_id: userId,
          alumni_id: id,
          service_id: selectedService,
          scheduled_at: new Date(`${selectedDate}T${selectedTime}`).toISOString(),
          status: "pending"
        })
        .select();

      if (bookingError) {
        console.error("Booking error:", bookingError);
        toast({
          title: "Booking failed",
          description: "There was an error creating your booking. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // If message is provided, create a conversation
      if (message.trim()) {
        // Check if there's an existing conversation
        const { data: existingConversation } = await supabase
          .from("conversations")
          .select("id")
          .eq("user_id", userId)
          .eq("alumni_id", id)
          .maybeSingle();

        let conversationId;
        if (existingConversation) {
          conversationId = existingConversation.id;
        } else {
          const { data: newConversation, error: convError } = await supabase
            .from("conversations")
            .insert({
              user_id: userId,
              alumni_id: id,
              booking_id: bookingData?.[0]?.id
            })
            .select();
          
          if (convError) {
            console.error("Error creating conversation:", convError);
          } else {
            conversationId = newConversation[0].id;
          }
        }

        // Send the message
        if (conversationId) {
          await supabase.from("messages").insert({
            conversation_id: conversationId,
            sender_id: userId,
            receiver_id: id,
            content: message
          });
        }
      }

      toast({
        title: "Booking request sent!",
        description: "The alumni will be notified of your booking request.",
      });
      
      setIsBookingOpen(false);
    } catch (error) {
      console.error("Booking process error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    }
  };

  const handleSendMessage = async () => {
    try {
      // Check if user is logged in
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to send a message",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }

      const userId = session.session.user.id;

      // Create or get conversation
      const { data: existingConversation } = await supabase
        .from("conversations")
        .select("id")
        .eq("user_id", userId)
        .eq("alumni_id", id)
        .maybeSingle();

      let conversationId;
      
      if (existingConversation) {
        conversationId = existingConversation.id;
      } else {
        const { data: newConversation, error: convError } = await supabase
          .from("conversations")
          .insert({
            user_id: userId,
            alumni_id: id
          })
          .select();
          
        if (convError) {
          console.error("Error creating conversation:", convError);
          toast({
            title: "Message failed",
            description: "There was an error sending your message. Please try again.",
            variant: "destructive"
          });
          return;
        }
        
        conversationId = newConversation[0].id;
      }

      // Send the message
      const { error: msgError } = await supabase
        .from("messages")
        .insert({
          conversation_id: conversationId,
          sender_id: userId,
          receiver_id: id,
          content: message
        });

      if (msgError) {
        console.error("Message error:", msgError);
        toast({
          title: "Message failed",
          description: "There was an error sending your message. Please try again.",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Message sent!",
        description: "Your message has been sent to the alumni.",
      });
      
      setIsMessageOpen(false);
      setMessage("");
    } catch (error) {
      console.error("Messaging error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    }
  };

  // Use data from state or from query
  const alumniData = alumniFromState || data?.profile;
  const services = data?.services || [];
  const reviews = data?.reviews || [];

  // Calculate whether we have data to display
  const hasData = !!alumniData || (!!data && !!data.profile);
  
  // Show loading state if we're loading and don't have state data
  if (isLoading && !alumniFromState) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-64 bg-gray-100 rounded-lg mb-8"></div>
            <div className="h-8 bg-gray-100 w-1/3 rounded mb-4"></div>
            <div className="h-4 bg-gray-100 w-2/3 rounded mb-8"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show error state
  if ((error && !alumniFromState) || (!isLoading && !hasData)) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="p-8 bg-red-50 text-red-500 rounded-lg text-center">
            <h2 className="text-xl font-semibold mb-2">Error Loading Alumni Profile</h2>
            <p>We couldn't load this alumni profile. Please try again later.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Calculate data for rendering
  const profile = alumniData;
  const fullName = profile?.name || (profile?.profiles 
    ? `${profile.profiles.first_name || ''} ${profile.profiles.last_name || ''}`.trim()
    : 'Alumni');
  
  const initials = fullName
    .split(' ')
    .map(name => name?.[0])
    .filter(Boolean)
    .join('')
    .toUpperCase();
  
  const avatarUrl = profile?.avatar_url || profile?.profiles?.avatar_url;
  
  // Calculate average rating
  const avgRating = reviews?.length 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    : 4.8; // Default rating if no reviews
  const roundedRating = Math.round(avgRating * 10) / 10;

  // Generate available times for demo
  const availableTimes = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar className="h-24 w-24 border border-gray-200">
              <AvatarImage src={avatarUrl || ''} />
              <AvatarFallback className="bg-crimson text-white text-xl font-medium">
                {initials || 'A'}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-grow">
              <h1 className="text-3xl font-bold text-gray-900">{fullName}</h1>
              <p className="text-lg text-gray-600 mt-1">{profile?.title}</p>
              
              <div className="flex flex-wrap gap-6 mt-4">
                {profile?.company && (
                  <div className="flex items-center">
                    <Building className="w-5 h-5 text-crimson mr-2" />
                    <span className="font-medium">{profile.company}</span>
                  </div>
                )}
                {profile?.school && (
                  <div className="flex items-center">
                    <School className="w-5 h-5 text-crimson mr-2" />
                    <span className="font-medium">{profile.school}</span>
                  </div>
                )}
                {profile?.years_experience && (
                  <div className="flex items-center">
                    <Briefcase className="w-5 h-5 text-crimson mr-2" />
                    <span>{profile.years_experience} Years Experience</span>
                  </div>
                )}
                {reviews?.length > 0 && (
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-yellow-500 mr-2" />
                    <span>{roundedRating.toFixed(1)} ({reviews.length} reviews)</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col gap-3 mt-4 md:mt-0">
              <Dialog open={isMessageOpen} onOpenChange={setIsMessageOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-crimson hover:bg-crimson-dark">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Message
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send a message to {fullName}</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <Label htmlFor="message">Your message</Label>
                    <Textarea 
                      id="message" 
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="mt-2"
                      placeholder="Write your message here..."
                      rows={5}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsMessageOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      className="bg-crimson hover:bg-crimson-dark"
                      onClick={handleSendMessage}
                    >
                      Send Message
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {profile?.availability_status && (
                <div className={`text-center px-4 py-2 rounded-md ${
                  profile.availability_status === 'available' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {profile.availability_status === 'available' ? 'Available' : 'Limited Availability'}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Main Content Tabs */}
        <Tabs defaultValue="about" className="mb-8">
          <TabsList className="mb-6">
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>
          
          {/* About Section */}
          <TabsContent value="about">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>About {fullName}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-2">Biography</h3>
                  <p className="text-gray-700 whitespace-pre-line">
                    {profile?.profiles?.bio || 'No bio available.'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Building className="mr-2 h-5 w-5 text-crimson" />
                      Professional Information
                    </h3>
                    <div className="space-y-4">
                      {profile?.company && (
                        <div>
                          <span className="text-sm text-gray-500 block">Company</span>
                          <span className="font-medium">{profile.company}</span>
                        </div>
                      )}
                      {profile?.title && (
                        <div>
                          <span className="text-sm text-gray-500 block">Position</span>
                          <span className="font-medium">{profile.title}</span>
                        </div>
                      )}
                      {profile?.years_experience && (
                        <div>
                          <span className="text-sm text-gray-500 block">Experience</span>
                          <span className="font-medium">{profile.years_experience} years</span>
                        </div>
                      )}
                      {profile?.expertise && (
                        <div>
                          <span className="text-sm text-gray-500 block">Areas of Expertise</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {profile.expertise.split(',').map((tag, i) => (
                              <Badge key={i} variant="outline" className="bg-gray-100">{tag.trim()}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <GraduationCap className="mr-2 h-5 w-5 text-crimson" />
                      Education
                    </h3>
                    <div className="space-y-4">
                      {profile?.school && (
                        <div>
                          <span className="text-sm text-gray-500 block">Institution</span>
                          <span className="font-medium">{profile.school}</span>
                        </div>
                      )}
                      {profile?.degree && (
                        <div>
                          <span className="text-sm text-gray-500 block">Degree</span>
                          <span className="font-medium">{profile.degree}</span>
                        </div>
                      )}
                      {profile?.field_of_study && (
                        <div>
                          <span className="text-sm text-gray-500 block">Field of Study</span>
                          <span className="font-medium">{profile.field_of_study}</span>
                        </div>
                      )}
                      {profile?.graduation_year && (
                        <div>
                          <span className="text-sm text-gray-500 block">Graduation Year</span>
                          <span className="font-medium">{profile.graduation_year}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Services Section */}
          <TabsContent value="services">
            <h2 className="text-2xl font-bold mb-6">Services Offered</h2>
            
            {services.length === 0 ? (
              <p className="text-gray-500">No services listed yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <Card key={service.id} className="overflow-hidden bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      <div className="bg-gray-50 p-6 border-b border-gray-100">
                        <h3 className="font-semibold text-xl mb-2">{service.title}</h3>
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-crimson">${service.price}</span>
                          <span className="text-sm text-gray-600 flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {service.duration || 60} mins
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <p className="text-gray-700 mb-6">
                          {service.description || "No description provided."}
                        </p>
                        
                        <Dialog open={isBookingOpen && selectedService === service.id} 
                            onOpenChange={(open) => {
                              if (open) {
                                setSelectedService(service.id);
                              }
                              setIsBookingOpen(open);
                          }}>
                          <DialogTrigger asChild>
                            <Button className="w-full bg-crimson hover:bg-crimson-dark">
                              Book Now
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[500px]">
                            <DialogHeader>
                              <DialogTitle>Book {service.title}</DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                              <div>
                                <Label htmlFor="date">Select Date</Label>
                                <Input
                                  id="date"
                                  type="date"
                                  className="mt-1"
                                  min={new Date().toISOString().split('T')[0]}
                                  value={selectedDate}
                                  onChange={(e) => setSelectedDate(e.target.value)}
                                />
                              </div>
                              <div>
                                <Label htmlFor="time">Select Time</Label>
                                <Select value={selectedTime} onValueChange={setSelectedTime}>
                                  <SelectTrigger id="time" className="mt-1">
                                    <SelectValue placeholder="Select a time" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableTimes.map(time => (
                                      <SelectItem key={time} value={time}>
                                        {time}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="booking-message">Message (Optional)</Label>
                                <Textarea
                                  id="booking-message"
                                  placeholder="Any specific requirements or questions..."
                                  className="mt-1"
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                />
                              </div>
                              <div className="flex justify-between items-center border-t pt-4 mt-2">
                                <div>
                                  <p className="font-semibold">Price</p>
                                  <p className="text-lg">${service.price}</p>
                                </div>
                                <div>
                                  <p className="font-semibold">Duration</p>
                                  <p>{service.duration || 60} minutes</p>
                                </div>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setIsBookingOpen(false)}>
                                Cancel
                              </Button>
                              <Button 
                                className="bg-crimson hover:bg-crimson-dark"
                                onClick={handleBookingRequest}
                                disabled={!selectedDate || !selectedTime}
                              >
                                Confirm Booking
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Reviews Section */}
          <TabsContent value="reviews">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Reviews {reviews.length > 0 && `(${reviews.length})`}</h2>
              <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className={i < Math.floor(roundedRating) ? "text-yellow-500 fill-yellow-500" : "text-gray-300"} />
                  ))}
                </div>
                <span className="ml-2 font-medium">{roundedRating.toFixed(1)}</span>
              </div>
            </div>
            
            {reviews.length === 0 ? (
              <p className="text-gray-500 py-4">No reviews yet.</p>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {reviews.map((review) => (
                  <Card key={review.id} className="bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={review.profiles?.avatar_url || ''} />
                          <AvatarFallback className="bg-gray-200 text-gray-700">
                            {review.profiles?.first_name?.[0]}{review.profiles?.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="ml-4 flex-grow">
                          <div className="flex justify-between items-center">
                            <h4 className="font-medium">
                              {review.profiles?.first_name} {review.profiles?.last_name}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <div className="flex my-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                size={14} 
                                className={i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                              />
                            ))}
                          </div>
                          
                          <p className="text-gray-700 mt-2">
                            {review.comment || "No comment provided."}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default AlumniProfile;
